import React from "react";
import { Helmet } from "react-helmet";
import Layout from "../components/Layout";
import "./css/PrivacyPolicy.css";

const PrivacyPolicy = () => {
  const pageTitle = "Privacy Policy | Dhandha Karle";
  const metaDescription =
    "Learn how Dhandha Karle protects your data. Our Privacy Policy outlines data collection, usage, and security practices for our POS, inventory, and invoice tools.";

  return (
    <div>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta
          name="keywords"
          content="privacy policy, Dhandha Karle, data protection, POS software, invoice generator, business software India"
        />
        <meta name="author" content="Dhandha Karle Team" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dhandhakarle.com/privacy" />
        <meta
          property="og:image"
          content="https://dhandhakarle.com/og-image.jpg"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <link rel="canonical" href="https://dhandhakarle.com/privacy" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      </Helmet>

      <section className="privacy-policy">
        <div className="policy-container">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last Updated: April 11, 2025</p>

          <p>
            At Dhandha Karle, we value your privacy and are committed to
            protecting your personal information. This Privacy Policy explains how
            we collect, use, disclose, and safeguard your data when you use our
            website, services, or software, including our POS system, inventory
            management tools, and free invoice generator.
          </p>

          <h2>1. Information We Collect</h2>
          <p>We may collect the following types of information:</p>
          <ul>
            <li>
              <strong>Personal Information</strong>: Name, email address, phone
              number, business details, and payment information when you register
              or use our services.
            </li>
            <li>
              <strong>Usage Data</strong>: Information about how you interact with
              our website and services, such as IP address, browser type, pages
              visited, and timestamps.
            </li>
            <li>
              <strong>Business Data</strong>: Data you input into our tools, such
              as inventory records, invoices, and customer details, to provide our
              services.
            </li>
          </ul>

          <h2>2. How We Use Your Information</h2>
          <p>We use your information to:</p>
          <ul>
            <li>Provide, maintain, and improve our services.</li>
            <li>Process payments and manage subscriptions.</li>
            <li>
              Communicate with you, including sending updates, support responses,
              and promotional offers.
            </li>
            <li>Analyze usage trends to enhance user experience.</li>
            <li>Comply with legal obligations.</li>
          </ul>

          <h2>3. Data Sharing and Disclosure</h2>
          <p>We do not sell your personal information. We may share data with:</p>
          <ul>
            <li>
              <strong>Service Providers</strong>: Third parties that assist with
              payment processing, hosting, or analytics (e.g., EmailJS for form
              submissions).
            </li>
            <li>
              <strong>Legal Authorities</strong>: When required by law or to
              protect our rights and users.
            </li>
          </ul>

          <h2>4. Data Security</h2>
          <p>
            We implement industry-standard security measures, including encryption
            and secure servers, to protect your data. However, no system is
            completely secure, and we cannot guarantee absolute security.
          </p>

          <h2>5. Your Rights</h2>
          <p>You have the right to:</p>
          <ul>
            <li>Access or correct your personal information.</li>
            <li>Request deletion of your data, subject to legal obligations.</li>
            <li>Opt out of promotional communications.</li>
          </ul>
          <p>
            To exercise these rights, contact us at{" "}
            <a href="mailto:support@dhandhakarle.com">
              support@dhandhakarle.com
            </a>
            .
          </p>

          <h2>6. Cookies and Tracking</h2>
          <p>
            We use cookies to enhance your experience, such as remembering login
            details and analyzing site usage. You can manage cookie preferences
            through your browser settings.
          </p>

          <h2>7. Third-Party Links</h2>
          <p>
            Our services may contain links to third-party websites (e.g., Instagram,
            LinkedIn). We are not responsible for their privacy practices.
          </p>

          <h2>8. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy periodically. Changes will be posted
            on this page with an updated “Last Updated” date. Continued use of our
            services constitutes acceptance of the updated policy.
          </p>

          <h2>9. Contact Us</h2>
          <p>
            For questions or concerns about this Privacy Policy, please contact:
          </p>
          <p>
            Dhandha Karle Team
            <br />
            Email:{" "}
            <a href="mailto:support@dhandhakarle.com">
              support@dhandhakarle.com
            </a>
          </p>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;